<script setup lang="ts">
const props = defineProps<{
  modelValue: number
}>()

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const displayValue = ref(props.modelValue.toString())

// Watch for external changes
watch(() => props.modelValue, (newVal) => {
  const current = Number.parseFloat(displayValue.value) || 0
  if (newVal !== current) {
    displayValue.value = newVal === 0 ? '0' : newVal.toString()
  }
})

function handleDigit(digit: string) {
  // Check if there's a decimal point and how many decimal places
  const decimalIndex = displayValue.value.indexOf('.')
  if (decimalIndex !== -1) {
    const decimalPlaces = displayValue.value.length - decimalIndex - 1
    // Prevent more than 2 decimal places
    if (decimalPlaces >= 2) {
      return
    }
  }

  if (displayValue.value === '0') {
    displayValue.value = digit
  }
  else {
    displayValue.value += digit
  }
  updateValue()
}

function handleDecimal() {
  if (!displayValue.value.includes('.')) {
    displayValue.value += '.'
  }
}

function handleClear() {
  displayValue.value = '0'
  updateValue()
}

function handleBackspace() {
  if (displayValue.value.length > 1) {
    displayValue.value = displayValue.value.slice(0, -1)
  }
  else {
    displayValue.value = '0'
  }
  updateValue()
}

function handleQuickAmount(amount: number) {
  displayValue.value = amount.toString()
  updateValue()
}

function updateValue() {
  const numValue = Number.parseFloat(displayValue.value) || 0
  emit('update:modelValue', numValue)
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <!-- Display -->
    <div class="text-3xl font-bold text-center p-4 bg-gray-100 rounded-lg border-2 border-gray-300">
      ${{ displayValue }}
    </div>

    <!-- Quick Amount Buttons -->
    <div class="grid grid-cols-4 gap-2 mb-2">
      <Button
        v-for="amount in [5, 10, 20, 50]"
        :key="amount"
        :label="`$${amount}`"
        severity="secondary"
        size="small"
        class="text-xs"
        @click="handleQuickAmount(amount)"
      />
    </div>

    <!-- Number Pad -->
    <div class="grid grid-cols-3 gap-2">
      <!-- Digits 1-9 -->
      <Button
        v-for="digit in [1, 2, 3, 4, 5, 6, 7, 8, 9]"
        :key="digit"
        :label="digit.toString()"
        size="large"
        class="h-14 text-xl font-semibold"
        @click="handleDigit(digit.toString())"
      />

      <!-- Bottom row: Clear, 0, Decimal -->
      <Button
        label="C"
        severity="danger"
        size="large"
        class="h-14 text-xl font-semibold"
        @click="handleClear"
      />
      <Button
        label="0"
        size="large"
        class="h-14 text-xl font-semibold"
        @click="handleDigit('0')"
      />
      <Button
        label="."
        size="large"
        class="h-14 text-xl font-semibold"
        @click="handleDecimal"
      />

      <!-- Backspace button spanning full width -->
      <Button
        label="⌫"
        severity="secondary"
        size="large"
        class="col-span-3 h-12 text-xl"
        @click="handleBackspace"
      />
    </div>
  </div>
</template>
